import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Layers3, Loader2, RefreshCcw, SlidersHorizontal } from "lucide-react";
import CreatorShell from "@/components/creator/CreatorShell";
import SongStudioTabs from "@/components/creator/SongStudioTabs";
import StemMixer from "@/components/creator/stems/StemMixer";
import { PartsPicker, ResplitNotice, SplitPanel } from "@/components/creator/stems/SplitControls";
import { getSongStems, splitSongStems } from "@/lib/studioApi";
import { proStudioPath } from "@/lib/studioMode";
import { safeApiError } from "@/lib/safeErrorText";

const POLL_MS = 3000;

/** Stem workspace — split a song into 4 or 6 parts, hear them together, and export each part as WAV or MP3. */
export default function StemWorkspacePage() {
  const { id } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [parts, setParts] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await getSongStems(id);
      setInfo(d);
      setParts((p) => p || [...d.sets].sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))[0]?.parts || 6);
    } catch (e) { setError(e?.response?.status === 404 ? "We couldn't find that song." : safeApiError(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  const running = (info?.sets || []).some((s) => s.status === "running" || s.pending?.status === "running");
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [running, load]);

  const split = async (again = false) => {
    if (again && !window.confirm(`Split this song into ${parts} parts again? Your current parts stay available until the new ones are ready.`)) return;
    setBusy(true);
    try { await splitSongStems(id, parts); await load(); }
    catch (e) { toast.error("Couldn't start the split", { description: safeApiError(e) }); }
    finally { setBusy(false); }
  };

  const back = `/create/song/${id}`;
  if (error) return <CreatorShell testId="stems-page" back="/create/projects" title="Hmm, that song isn't here" subtitle={error} />;
  if (!info) return <CreatorShell testId="stems-page" back={back}><p className="flex items-center gap-2 text-white/60"><Loader2 className="h-5 w-5 animate-spin" /> Opening your song…</p></CreatorShell>;
  const current = info.sets.find((s) => s.parts === parts) || null;
  return (
    <CreatorShell testId="stems-page" back={back} spine="mix" spineDone={["write", "generate"]} wide>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-300/90"><Layers3 className="h-3.5 w-3.5" /> Stem workspace</p>
          <h1 data-testid="stems-title" className="mt-3 font-display text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl">{info.song.title}</h1>
          <p className="mt-2 text-white/60">Split the song into its parts, balance them, and take each one away as WAV or MP3.</p>
        </div>
        <div className="flex flex-col items-start gap-3 sm:items-end">
        <SongStudioTabs songId={id} />
        {current?.status === "done" ? (
          <div className="flex flex-wrap gap-2">
            <button type="button" data-testid="stems-resplit" onClick={() => split(true)} disabled={busy || current.pending?.status === "running"} className="inline-flex items-center gap-1.5 rounded-full border border-white/12 px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-white/65 hover:text-white disabled:opacity-40">
              <RefreshCcw className="h-3.5 w-3.5" /> Split again
            </button>
            <Link to={proStudioPath(id, "stems")} data-testid="stems-open-pro" className="inline-flex items-center gap-1.5 rounded-full border border-white/12 px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-white/65 hover:text-white">
              <SlidersHorizontal className="h-3.5 w-3.5" /> Mix in the Advanced Studio
            </Link>
          </div>
        ) : null}
        </div>
      </div>
      <div className="space-y-6">
        <PartsPicker parts={parts} onPick={setParts} sets={info.sets} disabled={busy} />
        {current?.status === "done" && current.pending ? <ResplitNotice pending={current.pending} parts={parts} /> : null}
        {current?.status === "done" ? <StemMixer key={current.id} set={current} /> : <SplitPanel set={current} parts={parts} onSplit={() => split(false)} busy={busy} instrumental={info.song.instrumental} />}
      </div>
    </CreatorShell>
  );
}
